import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  KeyRound,
  ShieldCheck,
  Trash2,
  UserCircle,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";
import apiService from "../../services/api";

const CandidateAccountSettings: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteAccountConfirmation, setShowDeleteAccountConfirmation] =
    useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initials =
    `${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}` || "JS";

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    setError(null);

    try {
      await apiService.deleteCandidateProfile();
      setShowDeleteAccountConfirmation(false);
      logout();
      navigate("/auth/login", {
        replace: true,
        state: { message: "Your account has been deleted successfully." },
      });
    } catch (err: any) {
      console.error("Error deleting account:", err);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to delete account",
      );
      setShowDeleteAccountConfirmation(false);
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Account Settings
          </h1>
          <p className="mt-2 text-gray-600">
            Manage your account preferences, update your profile information
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <section className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-base font-semibold text-white">
              {initials.toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-gray-950">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="truncate text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
            <h2 className="text-base font-semibold text-gray-950">
              Account Controls
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            <div className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="flex min-w-0 gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50">
                  <KeyRound className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-950">
                    Password
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Change the password used for sign in.
                  </p>
                </div>
              </div>
              <Link
                to="/candidate/change-password"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 text-sm font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Change
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="flex flex-col gap-4 bg-red-50 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="flex min-w-0 gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-red-950">
                    Delete Account
                  </h3>
                  <p className="mt-1 text-sm text-red-700">
                   Permanently delete your job seeker account and all associated profile data.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setShowDeleteAccountConfirmation(true);
                }}
                disabled={deletingAccount}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        </section>

        <aside className="h-fit rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="text-base font-semibold text-gray-950">
              Account Summary
            </h2>
          </div>
          <div className="space-y-4 p-5">
            <div className="flex items-start gap-3">
              <UserCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase text-gray-500">
                  Name
                </p>
                <p className="truncate text-sm font-medium text-gray-950">
                  {user?.first_name} {user?.last_name}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                <p className="text-sm text-gray-600">
                  These settings apply to your jobseeker account.
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <ConfirmationModal
        isOpen={showDeleteAccountConfirmation}
        onClose={() => setShowDeleteAccountConfirmation(false)}
        onConfirm={handleDeleteAccount}
        title="Delete account"
        message="Are you sure you want to delete your account? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deletingAccount}
      />
    </div>
  );
};

export default CandidateAccountSettings;
